// App.js
import '../src/styles/global.css';
import '../src/styles/tokens.css';
import '../src/styles/buttons.css';
import '../src/styles/buttons-compat.css';
import '../src/styles/badges.css';
// R5 B2.5 — the form and card kit (ui/Field, FieldRow, CheckField,
// FormSection, Card) is app-wide, like the button and badge families above:
// loaded once in the entry chunk, so no route chunk carries its own copy
// and the code-split stylesheets never disagree about its order (a route
// that imported it beside racks-v2.css in the other order was a
// mini-css-extract "Conflicting order" warning — an ERROR under CI=true).
import './components/ui/kit.css';
// R5 B3 — ui/HelpTip's "?" (the sentence a card used to print), app-wide
// for the same reason as the kit.
import './components/ui/helptip.css';
// …and the identity line's (utils/identityLine): since B3 the lineage
// pickers draw it too, so it is reached from the Pairings chunk and the
// Animals chunk in opposite orders against inline.css / lineage.css — a
// "Conflicting order" (an ERROR under CI=true) unless it loads here, once.
import './styles/identity-line.css';
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppBootstrap } from './hooks/useAppBootstrap';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import EmailConfirmationSent from './components/Auth/EmailConfirmationSent';
import EmailVerified from './components/Auth/EmailVerified'; // ⬅️ add this near the top with other imports
import ResetPasswordRequest from './pages/auth/ResetPasswordRequest';
import ResetPasswordConfirm from './pages/auth/ResetPasswordConfirm';
import EmailPreferencesPage from './pages/EmailPreferencesPage';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import {
  FroggerDockMount, FroggyChatBubbleMount,
} from './components/layout/CornerMounts';
// R-TRAINING junior fix 3: a signed-in junior opening `/` lands on Froggy U.
import JuniorHomeLanding from './components/layout/JuniorHomeLanding';
// I-03 R6 · S3-06.3 — FroggerAI is a DOCK on every facility page, not a
// page of its own, and the page it is on is its current place.
import PlaceProvider from './context/PlaceContext';
import FeedingTabRedirect from './components/Feeding/FeedingTabRedirect';
import TaskSetupRedirect from './pages/facilities/TaskSetupRedirect';
import FacilityProvider from './context/FacilityContext';
import FacilityConsoleProvider from './context/FacilityConsoleContext';
import ThemeProvider from './context/ThemeProvider';
import { ModalStackProvider } from './context/ModalStackContext';
import { BrandingProvider } from './context/BrandingContext';
import { HelmetProvider } from 'react-helmet-async';
import TelemetryProvider from './components/TelemetryProvider';
import AboutUsPage from './pages/AboutUsPage';
import ProductsPage from './pages/ProductsPage';
import FrogsPage from './pages/FrogsPage';
import FrogGenusPage from './pages/FrogGenusPage';
import ProtectedRoute from './components/ProtectedRoute';
import PlantsPage from './pages/PlantsPage';
import GeckosPage from './pages/GeckosPage';
import FrogProductDetailPage from './pages/FrogProductDetailPage';
import SearchResultsPage from './pages/SearchResultsPage';
import CartPage from './pages/CartPage'; // 🛒 Added cart page route
import CheckoutAddressPage from './pages/CheckoutAddressPage';
import CheckoutPaymentPage from './pages/CheckoutPaymentPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import CustomerSchedule from './pages/CustomerSchedule';
import OrderScheduledPage from './pages/OrderScheduledPage';
import ShippingPolicyPage from './pages/ShippingPolicyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import FAQPage from './pages/FAQPage';
import CareGuidesPage from './pages/CareGuidesPage';
import SpeciesCareGuide from './pages/SpeciesCareGuide';
import FroggyChatPageStreaming from './pages/FroggyChatPageStreaming';
import HomePage from './pages/HomePage';
import PromoPage from './pages/PromoPage';
import NotFoundPage from './pages/NotFoundPage';
import FroggySpinner from './components/common/FroggySpinner';

// ── Facility console + operator pages (route-level code splitting, punch #119) ──
// Lazy-loaded so customers browsing the storefront never download the
// facility/admin console bundle. Providers, Navbar/Footer, auth pages, and
// every storefront page remain EAGER imports above — only console route
// elements are lazy. Each lazy() call becomes its own on-demand chunk;
// modules shared with the storefront stay in the main chunk automatically.
// The <Suspense> boundary wrapping <Routes> below uses the eagerly imported
// FroggySpinner as its fallback.
// Facility pages
const SpeciesPage = lazy(() => import('./pages/facilities/SpeciesPage'));
const AnimalsPage = lazy(() => import('./pages/facilities/AnimalsPage'));
const ClutchPage = lazy(() => import('./pages/facilities/ClutchPage'));
// I-03 R4 · S3-04.2 — who bred with whom.
const PairingsPage = lazy(() => import('./pages/facilities/PairingsPage'));
// I-03 R4 · S3-04.3 — the facility genetics calculator (proposal §4.4).
// The FACILITY-side precedent (a Route here, not a my-store `<Section>Page`
// wrapper), like `EventsSettingsPage` and `PairingsPage` before it.
const GeneticsCalculatorPage = lazy(
  () => import('./pages/facilities/GeneticsCalculatorPage'));
const RacksEnclosuresPage = lazy(() => import('./pages/facilities/RacksEnclosuresPage'));
const FacilityAIContextPage = lazy(() => import('./pages/facilities/FacilityAIContextPage'));
const FroggerAIPage = lazy(() => import('./pages/facilities/FroggerAIPage'));
// Task System pages (Phase C)
const TasksTodayPage = lazy(() => import('./pages/facilities/TasksTodayPage'));
// R5 B4 (rule 6) — Tasks is three tabs, Today | All tasks | Setup. Setup
// is ONE page holding the Templates and Schedules sections; their old
// routes redirect to it with the section anchored (TaskSetupRedirect).
const TaskSetupPage = lazy(() => import('./pages/facilities/TaskSetupPage'));
const TasksManagerPage = lazy(() => import('./pages/facilities/TasksManagerPage'));
// I-03 S3-07.1 (Fable ruling 6) — ONE Feeding page with tabs; the four
// old feeding routes redirect to their tab (FeedingTabRedirect).
const FeedingPage = lazy(() => import('./pages/facilities/FeedingPage'));
// R7-INVENTORY slice 1 — Facility → Supplies (spec §7), the bell's list
// and Settings → Notifications (R-NOTIFY §5.5).
const SuppliesPage = lazy(() => import('./pages/facilities/SuppliesPage'));
const NotificationsPage = lazy(() => import('./pages/facilities/NotificationsPage'));
const NotificationSettingsPage = lazy(() => import('./pages/facilities/NotificationSettingsPage'));
const EmployeeSchedulesPage = lazy(() => import('./pages/facilities/EmployeeSchedulesPage'));
const TrainingRegisterPage = lazy(() => import('./pages/facilities/TrainingRegisterPage'));
const FroggyUPage = lazy(() => import('./pages/facilities/FroggyUPage'));
const PipelineHistoryPage = lazy(() => import('./pages/facilities/PipelineHistoryPage'));
// I-03 R3 / S3-03.4 — the events catalog admin. A FACILITY-side page
// (husbandry configuration), so it follows the facility precedent —
// pages/facilities/<Page>.jsx + a Route here — not the my-store
// `<Section>Page` wrapper convention.
const EventsSettingsPage = lazy(() => import('./pages/facilities/EventsSettingsPage'));
// Store (operator console) pages
const ManageProductsPage = lazy(() => import('./pages/my-store/ManageProductsPage'));
const MediaLibraryPage = lazy(() => import('./pages/my-store/MediaLibraryPage'));
const ShippingSettingsPage = lazy(() => import('./pages/my-store/ShippingSettingsPage'));
const ShippingOrdersPage = lazy(() => import('./pages/my-store/ShippingOrdersPage'));
const OrdersSalesPage = lazy(() => import('./pages/my-store/OrdersSalesPage'));
const PromotionsPage = lazy(() => import('./pages/my-store/PromotionsPage'));
const MarketingPage = lazy(() => import('./pages/my-store/MarketingPage'));
const MarketingChatPage = lazy(() => import('./pages/my-store/MarketingChatPage'));
const OperatorTelemetryPage = lazy(() => import('./pages/my-store/OperatorTelemetryPage'));
const BusinessHealthPage = lazy(() => import('./pages/my-store/BusinessHealthPage'));
const CuttingsPage = lazy(() => import('./pages/my-store/CuttingsPage'));
const RecentActivityPage = lazy(() => import('./pages/my-store/RecentActivityPage'));
const ClientJourneyPage = lazy(() => import('./pages/my-store/ClientJourneyPage'));
const PipelineStatusPage = lazy(() => import('./pages/my-store/PipelineStatusPage'));
// Dev/engineering routes
const DevAIBundle = lazy(() => import('./pages/DevAIBundle'));

function BootFailurePanel({ code }) {
  const buildInfo = `${process.env.REACT_APP_GIT_COMMIT || 'dev'} @ ${process.env.REACT_APP_BUILD_TIME || 'local'}`;
  const handleReload = () => window.location.reload();
  const handleClearAndReload = () => {
    try { localStorage.clear(); } catch (_) { /* ignore */ }
    try { sessionStorage.clear(); } catch (_) { /* ignore */ }
    window.location.reload();
  };

  return (
    <div className="app-shell ff-theme" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', padding: '24px', textAlign: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{ maxWidth: 420 }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: 8 }}>
          Unable to load Feeling Froggy
        </h2>
        <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: 16 }}>
          The site couldn't connect to our servers. This is usually a temporary network issue.
        </p>
        <button onClick={handleReload} style={{
          padding: '10px 28px', fontSize: '1rem', fontWeight: 600,
          color: '#fff', background: '#28a745', border: 'none',
          borderRadius: 6, cursor: 'pointer', marginBottom: 12,
        }}>
          Reload Page
        </button>
        <br />
        <button onClick={handleClearAndReload} style={{
          padding: '6px 16px', fontSize: '0.85rem', color: '#555',
          background: 'transparent', border: '1px solid #ccc',
          borderRadius: 4, cursor: 'pointer', marginBottom: 20,
        }}>
          Clear Site Data &amp; Reload
        </button>
        <p style={{ fontSize: '0.75rem', color: '#999', marginTop: 12 }}>
          Code: {code} &middot; Build: {buildInfo}
          <br />
          Route: {window.location.pathname}
        </p>
      </div>
    </div>
  );
}

// Suspense fallback shown in the main content area while a lazily loaded
// route chunk downloads (facility console / my-store / dev routes). Matches
// the boot-time FroggySpinner treatment so chunk loads stay on-brand.
// Navbar and Footer remain mounted around it.
function RouteChunkFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
      <FroggySpinner size="lg" label="Loading" />
    </div>
  );
}

/*
 * `FroggyChatBubbleMount` and `FroggerDockMount` moved to
 * `components/layout/CornerMounts.jsx` (S3-06.3c review).
 *
 * They are the two bottom-right fixed corner mounts and they decide from
 * ONE predicate — the pill must stand down on exactly the routes the
 * dock claims, because the pill is the higher layer and covers the
 * dock's mic and send buttons. Living in one file is what lets that be
 * tested by RENDERING them, rather than by reading this file's source.
 * They are still rendered here, as siblings of <main>.
 */

function App() {
  // Bootstrap app on mount and on route changes
  const bootstrapState = useAppBootstrap();

  // Show loading state only on initial mount. Uses FroggySpinner
  // (branded logo + pulse) so the app's first paint is on-brand,
  // not a bare "Loading..." text (2026-07-18 polish).
  if (bootstrapState.loading) {
    return (
      <div className="app-shell ff-theme" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <FroggySpinner size="lg" label="Loading Feeling Froggy" />
      </div>
    );
  }

  // Boot watchdog — show actionable error panel instead of stuck loading
  if (bootstrapState.error) {
    return <BootFailurePanel code={bootstrapState.error} />;
  }

  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrandingProvider>
          <ModalStackProvider>
            <FacilityConsoleProvider>
              <FacilityProvider>
        <TelemetryProvider>
        {/* I-03 R6 · S3-06.3 — the current place. It wraps the routes
            AND the dock: the pages set it, the dock reads it. */}
        <PlaceProvider>
        <div className="app-shell ff-theme">
          <JuniorHomeLanding />
          <Navbar />
          <main className="app-content">
            <Suspense fallback={<RouteChunkFallback />}>
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/promo/:slug" element={<PromoPage />} />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/email-confirmation-sent" element={<EmailConfirmationSent />} />
            <Route path="/email-verified" element={<EmailVerified />} />
            <Route path="/reset-password" element={<ResetPasswordRequest />} />
            <Route path="/reset-password/:uid/:token" element={<ResetPasswordConfirm />} />
            <Route path="/email-preferences/:token" element={<EmailPreferencesPage />} />

            {/* Storefront Pages */}
            <Route path="/products" element={<ProductsPage />} />
            {/* #37 storefront search results */}
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/frogs" element={<FrogsPage />} />
            <Route path="/frogs/products/:productId" element={<FrogProductDetailPage />} />
            <Route path="/frogs/:genus" element={<FrogGenusPage />} />
            <Route path="/plants" element={<PlantsPage />} />
            <Route path="/geckos" element={<GeckosPage />} />
            <Route path="/geckos/:genus" element={<FrogGenusPage kind="geckos" />} />
            {/* Legacy /aquatics redirect to /geckos (product-line
                pivot 2026-05-30). React Router can't issue a true
                301, but Navigate replace:true preserves bookmark UX
                + signals deprecation to the in-app router. Pair
                with a server-side 301 in Railway/Cloudflare if
                SEO juice from the old URL needs to fully transfer. */}
            <Route path="/aquatics" element={<Navigate to="/geckos" replace />} />
            <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/care-guides" element={<CareGuidesPage />} />
            <Route path="/care-guides/:speciesSlug" element={<SpeciesCareGuide />} />

            {/* Cart */}
            <Route path="/cart" element={<CartPage />} />
            <Route
              path="/checkout/address"
              element={
                <ProtectedRoute>
                  <CheckoutAddressPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/payment"
              element={
                <ProtectedRoute>
                  <CheckoutPaymentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/order-confirmation/:orderId"
              element={
                <ProtectedRoute>
                  <OrderConfirmationPage />
                </ProtectedRoute>
              }
            />

            {/* Customer Account Pages */}
            {/* #159: /orders is the natural guess URL — redirect it rather
                than dead-ending at NotFound. The real page is protected;
                anonymous visitors bounce to /login with a return path. */}
            <Route path="/orders" element={<Navigate to="/account/orders" replace />} />
            <Route
              path="/account/orders"
              element={
                <ProtectedRoute>
                  <OrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/orders/:orderId"
              element={
                <ProtectedRoute>
                  <OrderDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/orders/:orderId/schedule"
              element={
                <ProtectedRoute>
                  <CustomerSchedule />
                </ProtectedRoute>
              }
            />
            {/* Punch #44b: token-based scheduling for account-less customers
                (manual orders). The token IS the credential — no login. */}
            <Route
              path="/schedule/:token"
              element={<CustomerSchedule />}
            />
            <Route
              path="/account/orders/:orderId/scheduled"
              element={
                <ProtectedRoute>
                  <OrderScheduledPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/froggy-chat"
              element={
                <ProtectedRoute>
                  <FroggyChatPageStreaming />
                </ProtectedRoute>
              }
            />

            {/* Legacy route redirects */}
            <Route path="/froggy-ai" element={<Navigate to="/account/froggy-chat" replace />} />
            <Route path="/account/froggy-ai" element={<Navigate to="/account/froggy-chat" replace />} />
            <Route path="/account/froggy" element={<Navigate to="/account/froggy-chat" replace />} />

            {/* Dev routes */}
            <Route path="/dev/ai-bundle" element={<ProtectedRoute><DevAIBundle /></ProtectedRoute>} />

            {/* Facility / Store / Admin Pages */}
            {/* Facility routes with sections */}
            <Route path="/facilities/:facilityId" element={<Navigate to="species" replace />} />
            <Route path="/facilities/:facilityId/species" element={<ProtectedRoute><SpeciesPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/animals" element={<ProtectedRoute><AnimalsPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/clutch" element={<ProtectedRoute><ClutchPage /></ProtectedRoute>} />
            {/* I-03 R4 · S3-04.2 — the Pairings page (proposal 4.2).
                A FACILITY-side page plus a Route, the
                `EventsSettingsPage` precedent — not the my-store
                `<Section>Page` wrapper, which belongs to the storefront. */}
            <Route path="/facilities/:facilityId/pairings" element={<ProtectedRoute><PairingsPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/genetics/calculator" element={<ProtectedRoute><GeneticsCalculatorPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/racks-enclosures" element={<ProtectedRoute><RacksEnclosuresPage /></ProtectedRoute>} />
            {/* S3-02.3b — a support room's own map. Declared BEFORE the
                :rackId route for readability; the two cannot collide
                anyway (three path segments after the facility, not two). */}
            <Route path="/facilities/:facilityId/racks-enclosures/rooms/:roomId" element={<ProtectedRoute><RacksEnclosuresPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/racks-enclosures/:rackId" element={<ProtectedRoute><RacksEnclosuresPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/ai-context" element={<ProtectedRoute><FacilityAIContextPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/frogger-ai" element={<ProtectedRoute><FroggerAIPage /></ProtectedRoute>} />
            {/* Task System routes (Phase C) */}
            <Route path="/facilities/:facilityId/tasks" element={<ProtectedRoute><TasksTodayPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/tasks/today" element={<ProtectedRoute><TasksTodayPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/tasks/all" element={<ProtectedRoute><TasksManagerPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/tasks/setup" element={<ProtectedRoute><TaskSetupPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/tasks/templates" element={<TaskSetupRedirect section="templates" />} />
            <Route path="/facilities/:facilityId/tasks/schedules" element={<TaskSetupRedirect section="schedules" />} />
            {/* I-03 S3-07.1 — ONE Feeding page, `?tab=plan|history|foods|profiles`.
                The old routes REDIRECT to their tab, query string kept. */}
            <Route path="/facilities/:facilityId/feeding" element={<ProtectedRoute><FeedingPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/feeding-profiles" element={<FeedingTabRedirect tab="profiles" />} />
            <Route path="/facilities/:facilityId/feeding-plan" element={<FeedingTabRedirect tab="plan" />} />
            <Route path="/facilities/:facilityId/feeding-history" element={<FeedingTabRedirect tab="history" />} />
            <Route path="/facilities/:facilityId/feeder-status" element={<FeedingTabRedirect tab="foods" />} />
            {/* R7-INVENTORY slice 1 — Supplies (`?tab=supplies|purchases|suppliers|shopping`,
                `&item=<id>` lands on one supply), the bell's list, and
                Settings → Notifications. */}
            <Route path="/facilities/:facilityId/supplies" element={<ProtectedRoute><SuppliesPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/notification-settings" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/employee-schedules" element={<ProtectedRoute><EmployeeSchedulesPage /></ProtectedRoute>} />
            {/* R-TRAINING slice 1 — the training register (`?tab=register|review|keeper&keeper=<id>`)
                and Froggy U (`?tab=day|training|learn`, `?as=<id>` read-only). */}
            <Route path="/facilities/:facilityId/training" element={<ProtectedRoute><TrainingRegisterPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/froggy-u" element={<ProtectedRoute><FroggyUPage /></ProtectedRoute>} />
            <Route path="/facilities/:facilityId/pipeline" element={<ProtectedRoute><PipelineHistoryPage /></ProtectedRoute>} />
            {/* I-03 S3-03.4 — Types / Routines / Rules. `?tab=` picks the
                tab, so a deep link from a task's "because" line can land
                on the rule that made it. */}
            <Route path="/facilities/:facilityId/events-settings" element={<ProtectedRoute><EventsSettingsPage /></ProtectedRoute>} />

            {/* Store routes with sections */}
            <Route path="/my-store/:facilityId" element={<Navigate to="manage-products" replace />} />
            <Route path="/my-store/:facilityId/manage-products" element={<ProtectedRoute><ManageProductsPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/images" element={<ProtectedRoute><MediaLibraryPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/shipping-settings" element={<ProtectedRoute><ShippingSettingsPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/shipping-orders" element={<ProtectedRoute><ShippingOrdersPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/sales-history" element={<ProtectedRoute><OrdersSalesPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/promotions" element={<ProtectedRoute><PromotionsPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/cuttings" element={<ProtectedRoute><CuttingsPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/marketing" element={<ProtectedRoute><MarketingPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/marketing/chat" element={<ProtectedRoute><MarketingChatPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/operator-telemetry" element={<ProtectedRoute><OperatorTelemetryPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/business-health" element={<ProtectedRoute><BusinessHealthPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/recent-activity" element={<ProtectedRoute><RecentActivityPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/client-journey" element={<ProtectedRoute><ClientJourneyPage /></ProtectedRoute>} />
            <Route path="/my-store/:facilityId/pipeline-status" element={<ProtectedRoute><PipelineStatusPage /></ProtectedRoute>} />

            {/* Catch-all 404. Must be last. Renders a branded NotFound page
                instead of a blank main area when the user hits a typoed URL
                or an unregistered path. Caught during S12 sweep 2026-07-15
                where /my-store/<fid>/animals (wrong prefix) silently blanked. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
            </Suspense>
          </main>
          <Footer className="app-footer" />
          <FroggyChatBubbleMount />
          <FroggerDockMount />
        </div>
        </PlaceProvider>
        </TelemetryProvider>
              </FacilityProvider>
            </FacilityConsoleProvider>
          </ModalStackProvider>
        </BrandingProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;